// Today's pods at a station, split the way the floor is: one lane per flowline,
// and each lane cut at the line's number for the day.
//
// The queue and the finished list both arrive flat, ordered across every line
// the station feeds. That is the right answer to "what next" and the wrong one
// to "where am I up to": #7 of a flat list of 12 tells an operator nothing
// when three lines are feeding them.
//
// So the day is regrouped per flowline, put back into production sequence
// with the finished and the still-to-do pods interleaved — signing a pod off
// out of turn does not move it to the top, it stays where the plan had it,
// which is how the gap it left is seen — and numbered 1..n, where n is what
// this station is asked for on that line today: its own target where it has
// one, the flowline's plan where it does not. The pods beyond n are real and
// still tappable, but they are tomorrow's: they sit apart so that "how is today
// going" is not answered by a list that never ends, and a pod signed off from
// beyond n stays beyond it.

import type { CompletedPodVM, PodVM } from '@/api/types';

/** One line of a lane's day: a pod signed off, or one still to do. */
export type DayRow =
  | { kind: 'done'; pod: CompletedPodVM; position: number }
  | { kind: 'queued'; pod: PodVM; position: number };

/** One flowline's whole day at this work centre: what is done and what is left. */
export interface FlowlineDay {
  /** null is the lane for pods Odoo has not put on a flowline. */
  id: number | null;
  name: string;
  short: string;
  /** Every finished pod of this line's here today, in production sequence. */
  done: CompletedPodVM[];
  /** Everything still to do, in production sequence. */
  queued: PodVM[];
  /** Today's plan for this line here, or null when nobody has set one. */
  target: number | null;
  /** The n the day counts to — the target, or what is here when there is none. */
  n: number;
  /**
   * The lane in production sequence, done and queued together, each row
   * carrying its number in the day. `today` is the first n of them; `future`
   * is the rest, numbered on from where today stops.
   */
  today: DayRow[];
  future: DayRow[];
  /** done + queued — everything of this line's that is at the station today. */
  total: number;
}

export interface ScopeLine {
  id: number;
  name: string;
  short: string;
  /** Today's target: the station's own where it has one, else the flowline's. */
  target?: number | null;
}

export interface DaySplitOptions {
  /**
   * A pod that must stay in today's list wherever the number falls — the one
   * the operator has started. Hiding the pod in someone's hands behind a
   * disclosure is never the right answer, whatever the plan says.
   */
  keepVisibleWoId?: number | null;
}

/** "FL 2" — the fallback for a line the scope has not named. */
export function shortLabel(name: string): string {
  return name.replace(/^Flowline\s*/i, 'FL ').replace(/^FL-\s*/i, 'FL ').trim();
}

/**
 * The day, lane by lane.
 *
 * Every line in `scope` gets a lane whether or not it has work here today: the
 * tab strip must not reshuffle itself under the operator's thumb each time a
 * pod moves. Lines the scope does not know about — and pods with no line at
 * all — are added after them, so nothing is dropped on the floor.
 */
export function flowlineDays(
  queued: readonly PodVM[],
  done: readonly CompletedPodVM[],
  scope: readonly ScopeLine[],
  unassignedLabel: string,
  opts: DaySplitOptions = {},
): FlowlineDay[] {
  const lanes = new Map<number | null, FlowlineDay>();
  const lane = (id: number | null, name: string | null): FlowlineDay => {
    const found = lanes.get(id);
    if (found) return found;
    const label = id == null ? unassignedLabel : (name ?? unassignedLabel);
    const made: FlowlineDay = {
      id,
      name: label,
      short: id == null ? unassignedLabel : shortLabel(label),
      done: [],
      queued: [],
      target: null,
      n: 0,
      today: [],
      future: [],
      total: 0,
    };
    lanes.set(id, made);
    return made;
  };

  for (const line of scope) {
    const made = lane(line.id, line.name);
    made.name = line.name;
    made.short = line.short;
    made.target = line.target ?? null;
  }
  for (const pod of done) lane(pod.flowlineId, pod.flowlineName).done.push(pod);
  for (const pod of queued) lane(pod.flowlineId, pod.flowlineName).queued.push(pod);

  const order = new Map(scope.map((line, i) => [line.id as number | null, i]));
  return [...lanes.values()]
    .map((l) => sequenceAndCut(l, opts))
    .sort((a, b) => rank(a, order) - rank(b, order) || compareId(a.id, b.id));
}

/**
 * Put the lane in production sequence and cut it where today's number runs
 * out.
 *
 * The sequence is Odoo's `production_seq`, the same key the queue is served
 * in, with the work order id behind it for the pods that share one. The
 * finished pods arrive newest-first from a date-ordered read; sorting them
 * back in here is what lets a pod finished out of turn keep its place.
 *
 * No target — or a target of nothing, which is a row somebody left at zero
 * rather than a plan — and the day is simply what is here: every pod counts as
 * today's, exactly as it did before targets were read at all.
 */
function sequenceAndCut(day: FlowlineDay, opts: DaySplitOptions): FlowlineDay {
  const rows: DayRow[] = [
    ...day.done.map((pod): DayRow => ({ kind: 'done', pod, position: 0 })),
    ...day.queued.map((pod): DayRow => ({ kind: 'queued', pod, position: 0 })),
  ]
    .sort((a, b) => a.pod.productionSeq - b.pod.productionSeq || a.pod.woId - b.pod.woId)
    .map((r, i) => ({ ...r, position: i + 1 }) as DayRow);

  const total = rows.length;
  const planned = day.target != null && day.target > 0 ? day.target : null;
  const n = planned ?? total;

  let cut = Math.min(n, total);
  const started =
    opts.keepVisibleWoId == null
      ? -1
      : rows.findIndex((r) => r.kind === 'queued' && r.pod.woId === opts.keepVisibleWoId);
  if (started >= cut) cut = started + 1;

  return {
    ...day,
    done: rows.filter((r): r is Extract<DayRow, { kind: 'done' }> => r.kind === 'done').map((r) => r.pod),
    queued: rows.filter((r): r is Extract<DayRow, { kind: 'queued' }> => r.kind === 'queued').map((r) => r.pod),
    n,
    total,
    today: rows.slice(0, cut),
    future: rows.slice(cut),
  };
}

/** How many of today's rows are signed off — the "3/8" on a tab. */
export function doneInDay(day: FlowlineDay): number {
  return day.today.filter((r) => r.kind === 'done').length;
}

/** Scope order first, then lines the scope missed, then the no-flowline lane. */
function rank(day: FlowlineDay, order: Map<number | null, number>): number {
  const known = order.get(day.id);
  if (known != null) return known;
  return day.id == null ? Number.MAX_SAFE_INTEGER : order.size + 1;
}

function compareId(a: number | null, b: number | null): number {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  return a - b;
}
